// Core packages
import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { FormGroup } from "@angular/forms";

// Third party packages
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from "@angular/material";
import { FormlyFieldConfig, FormlyFormOptions } from "@ngx-formly/core";
import { TranslateService } from "@ngx-translate/core";
import { MatProgressButtonOptions } from "mat-progress-buttons";
import { CookieService } from "ngx-cookie-service";
import { ToastrService } from "ngx-toastr";
import { Observable, Subscription } from "rxjs";
import { take } from "rxjs/operators";

// Custom packages
import { AuthService } from "src/app/modules/auth/auth.service";
import { ActivationsService } from "src/app/modules/settings/activations.service";
import { SettingsService } from "src/app/modules/settings/settings.service";
import { ConfirmDialogComponent } from "src/app/shared/components/dialogs/confirm-dialog/confirm-dialog.component";
import { ConfigService } from "src/app/shared/services/config.service";
import { HelperService } from "src/app/shared/services/helper.service";

interface DocsResponse {
  value: string;
  label: string;
}

/**
 * Script start
 */
@Component({
  selector: "app-activation-edit-dialog",
  templateUrl: "./activation-edit-dialog.component.html",
  styleUrls: ["./activation-edit-dialog.component.scss"],
})
export class ActivationEditDialogComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  lang = this.translateService.currentLang || this.translateService.defaultLang;
  dialogIsLoading = true; // Used to view/hide form loading spinner
  title = "";

  // BEGIN - Edit item form
  // BEGIN - ngx-formly
  form: FormGroup = new FormGroup({});
  docForm: FormGroup = new FormGroup({});
  model: any = {};
  docModel: any = {};
  fields: FormlyFieldConfig[] = [];
  docFields: FormlyFieldConfig[] = [];
  options: FormlyFormOptions = {};
  docOptions: FormlyFormOptions = {};

  // END - ngx-formly
  formBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant("common.cta.save"),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: "primary",
    spinnerColor: "primary",
    fullWidth: false,
    disabled: false,
    mode: "indeterminate",
    buttonIcon: {
      fontSet: "fal",
      fontIcon: "fa-save",
      inline: true,
    },
  };
  // END - Edit item form

  documents: any[] = [];

  /**
   * Class constructor
   */
  constructor(
    public dialogRef: MatDialogRef<ActivationEditDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialog: MatDialog,
    private configService: ConfigService,
    private translateService: TranslateService,
    private authService: AuthService,
    private cookieService: CookieService,
    private toastrService: ToastrService,
    private helperService: HelperService,
    private settingsService: SettingsService,
    private activationsService: ActivationsService
  ) { }


  /**
   * Init components
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    if (this.data.new) {
      this.title = 'Nuova attivazione';
    } else {
      this.title = 'Modifica attivazione';
    }

    const documents: any[] = [];
    if (
      this.data.documentTemplate &&
      Array.isArray(this.data.documentTemplate)
    ) {
      for (const contents of this.data.documentTemplate) {
        if (
          contents &&
          contents.content &&
          Array.isArray(contents.content)
        ) {
          for (const docs of contents.content) {
            const title = docs.title.replace('properties.documents.', '');
            const translationTitle = docs.translation && docs.translation.it ? docs.translation.it : '';
            if (docs.documents && Array.isArray(docs.documents)) {
              for (const doc of docs.documents) {
                const translationKey = doc.translation && doc.translation.it ? doc.translation.it : '';
                this.documents.push({
                  value: `${title}#-#${doc.key}`,
                  label: `${translationTitle}: ${translationKey}`,
                });
              }
            }
          }
        }
      }
    }

    const params: any[] = [];
    const options: any[] = [];
    options.push({
      value: '',
      label: '--- Seleziona un valore ---',
    });

    if (
      this.data.fieldGroup &&
      Array.isArray(this.data.fieldGroup)
    ) {
      for (const item of this.data.fieldGroup) {
        if (item.key !== 'note') {

          if (
            item.templateOptions &&
            item.templateOptions.options &&
            Array.isArray(item.templateOptions.options)
          ) {
            for (const option of item.templateOptions.options) {
              options.push({
                value: option.value,
                label: option.translation.it
              });
            }
          }
          if (
            item.templateOptions &&
            item.templateOptions.translationLabel &&
            item.templateOptions.translationLabel.it
          ) {
            params.push({
              value: item.key,
              label: item.templateOptions.translationLabel.it
            });
          }
        }
      }
    }
    this.initForm(params, options);
  }

  /**
   * Get docuemnts
   *
   * @since 1.0.0
   */
  getDocuments(start?: number, limit?: number, sort?: string, sortVersus?: string, search?: any): Observable<DocsResponse> {
    // this.documents;
    return new Observable<any>(obs => {
      return obs.next({ value: '1', label: 'ciao'});
    });
  }


  /**
   * Init form
   *
   * @since 1.0.0
   */
  initForm(params: any[], options: any[]): void {
    if (this.data.new) {
      this.fields = [];
      this.model = [];
      this.docFields = [];
      this.docModel = [];
    } else {
      this.data.activations.fields.forEach((el: any) => {
        el.option = el.value;
      });

      this.fields = [];
      this.model.structure = this.data.activations.fields;
      this.docFields = [];
      const initDocs = [];
      this.data.activations.docs.forEach((el: any) => {
        el.keys.forEach((subEl: any) => {
          initDocs.push({
            name: { 
              value: `${el.name}#-#${subEl}`,
              label: `${el.name}#-#${subEl}`,
            },
          });
        });
      });
      this.docModel.documents = initDocs;
    }

    this.fields = [
      {
        key: 'structure',
        type: 'repeat-structure-options',
        templateOptions: {
          addText: 'Aggiungi una nuova relazione',
        },
        fieldArray: {
          fieldGroupClassName: "row",
          fieldGroup: [
            {
              className: "col-3",
              type: "select",
              key: "name",
              templateOptions: {
                label: "Parametro",
                placeholder: "Parametro",
                required: true,
                multiple: false,
                options: params,
              },
            },
            {
              className: "col-1",
              type: "select",
              key: "operation",
              templateOptions: {
                label: "Operazione",
                placeholder: "Operazione",
                required: true,
                multiple: false,
                options: [
                  {
                    value: "<",
                    label: "<",
                  },
                  {
                    value: "<=",
                    label: "<=",
                  },
                  {
                    value: "=",
                    label: "=",
                  },
                  {
                    value: ">",
                    label: ">",
                  },
                  {
                    value: ">=",
                    label: ">=",
                  },
                ],
              },
            },
            {
              className: "col-3",
              type: "select",
              key: "option",
              templateOptions: {
                label: "Opzioni",
                placeholder: "Opzioni",
                required: false,
                multiple: false,
                options,
              },
              // expressionProperties: {
              //   "templateOptions.required":
              //     "model.structure && model.structure.value === ''",
              // },
            },
            {
              className: "col-2",
              type: "input",
              key: "value",
              templateOptions: {
                label: "Valore",
                placeholder: "Valore",
                required: false,
                minLength: 2,
                maxLength: 500,
              },
              defaultValue: "",
              // expressionProperties: {
              //   "templateOptions.required": (): boolean => {
              //     return (this.model.structure && this.model.structure.option === '');
              //   },
              // },
            },
          ],
        },
      },
    ];

    this.docFields = [
      {
        key: 'documents',
        type: 'repeat-structure-options',
        templateOptions: {
          addText: 'Aggiungi un documento',
        },
        fieldArray: {
          fieldGroupClassName: "row",
          fieldGroup: [
            {
              className: "col-12",
              type: "autocomplete",
              key: "name",
              templateOptions: {
                label: "Nome del documento da attivare",
                placeholder: "Nome del documento da attivare",
                required: true,
                // multiple: false,
                // options: documents,
                bindLabel: "label",
                bindValue: "value",
                // initialValues: [this.docModel.documents.name],
                multiple: false,
                search$: this.getDocuments.bind(this),
                typeToSearchText: this.translateService.instant(
                  "common.words.typeToSearch",
                  { minTermLength: 1 }
                ),
                clearAllText: this.translateService.instant(
                  "common.words.clearAll"
                ),
                notFoundText: this.translateService.instant(
                  "common.words.notFound"
                ),
                loadingText: this.translateService.instant(
                  "common.words.loading"
                ),
              },
            },
          ],
        },
      },
    ];
    this.dialogIsLoading = false;
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  /**
   * Handle dialog denial
   *
   * @since 1.0.0
   */
  onSave(): void {
    if (
      !this.model.structure || 
      !Array.isArray(this.model.structure) ||
      !this.model.structure.length ||
      !this.docModel.documents ||
      !Array.isArray(this.docModel.documents) ||
      !this.docModel.documents.length
    ) {
      this.toastrService.error(
        this.translateService.instant("common.errors.form_is_invalid")
      );
      return;
    }

    const structures: any[] = [];
    for (const structure of this.model.structure) {
      let value = structure.value;
      if (structure.option !== '') {
        value = structure.option;
      }
      structures.push({
        name: structure.name,
        operation: structure.operation,
        value,
      });
    }


    const docs: any[] = [];
    for (const doc of this.docModel.documents) {
      const docKey = doc.name.split('#-#');
      const index = docs.findIndex((el: any) => el.name === docKey[0]);
      if (index > -1) {
        docs[index].key.push({
          name: docKey[1]
        });
      } else {
        docs.push({
          name: docKey[0],
          key: [
            {
              name: docKey[1],
            }
          ],
        })
      }
    }

    const newActivation = {
      structure: this.data.structure, // "anagrafica",
      relation: "AND",
      fields: structures,
      docs,
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('settings.activation.dialog.create.title'),
        message: this.translateService.instant('settings.activation.dialog.create.message'),
        btnOkText: this.translateService.instant("common.cta.confirm"),
        btnCancelText: this.translateService.instant('common.cta.close'),
      },
      width: '500px',
      disableClose: true
    });
    // Subscribe to dialog result (only for 1 emit thanks to "take()")
    dialogRef.beforeClosed()
      .pipe(
        take(1)
      )
      .subscribe(
        res => {
          if (res) {
            if (this.data.new) {
              this.activationsService.create(newActivation)
                .subscribe(
                  (res) => {
                    if (res.status) {
                    }
                    this.dialogIsLoading = false;
                    this.toastrService.success(res.message);
                  },
                  (err) => {
                    this.helperService.handleError(err);
                    this.dialogIsLoading = false;
                  }
                )
              this.dialogRef.close(true);
            } else {
              this.activationsService.update(this.data.id, newActivation)
                .subscribe(
                  (res) => {
                    if (res.status) {
                    }
                    this.dialogIsLoading = false;
                    this.toastrService.success(res.message);
                  },
                  (err) => {
                    this.helperService.handleError(err);
                    this.dialogIsLoading = false;
                  }
                )
              this.dialogRef.close(true);
            }
          }
        }
      );
    
  }

  /**
   * Handle dialog denial
   *
   * @since 1.0.0
   */
  onClose(): void {
    this.dialogRef.close(false);
  }
}
